/**
 * New-owner arrival notice — per-user receipts, mirroring
 * `estimateResponseSummary.js`'s ANNOUNCED-vs-ACKNOWLEDGED map pattern
 * exactly (founder decision, 2026-09-24: "must persist EXACTLY like the
 * estimate-responses notice").
 *
 * Two independent maps, both `{ event_id: isoInstant }`, both per-user
 * localStorage (never a shared `:anon` bucket — no user id, no read/write):
 *
 *   ANNOUNCED — the greeting has been shown once (auto-open OR a manual
 *   reopen). Never re-opens automatically for this event_id again, but the
 *   Equipo nav badge / Team row pill / hero mark all stay lit — this is
 *   "Later", not "seen and done".
 *
 *   ACKNOWLEDGED — "Entendido" or "Ver equipo". Clears the badge/pill/mark
 *   too (`pending` in the hook is `notice exists AND not acknowledged`).
 *   Always also writes ANNOUNCED — an acknowledged event is, definitionally,
 *   an announced one.
 *
 * A single-key map (rather than a Set, as `workerTransferNotice.js` uses) is
 * the deliberate mirror of `estimateResponseSummary.js`'s `readStampMap`
 * shape — there is in practice at most one live event_id at a time, but the
 * map form costs nothing and keeps this file's shape identical to the one it
 * is told to mirror, including the 30-day prune-on-read floor. Each writer
 * RETURNS the resulting map (not a boolean) so a caller can `setState`
 * directly from it, same as `workerTransferNotice.js`'s
 * `acknowledgeWorkerTransfers`.
 *
 * RD-NOTICES-V2: write-throughs to the server as `owner_transfer:<eventId>`
 * (announced_at / acked_at). `opts.skipSync` and the merge/collect exports
 * follow the same shape as every other store in this feature.
 */
import { upsertNoticeReceipts } from '../../services/noticeReceipts';
import { isNewerIso, maxIso } from '../notices/noticeReceiptMath';
import { tenantIdForNoticeWrite } from '../notices/noticeReceiptsSession';

const OWNER_TRANSFER_PREFIX = 'owner_transfer:';
const MAX_LOOKBACK_MS = 30 * 24 * 60 * 60 * 1000;

export function ownerTransferAnnouncedKey(userId) {
  return `cvx-owner-transfer-announced-v1:${userId || 'anon'}`;
}

export function ownerTransferAckedKey(userId) {
  return `cvx-owner-transfer-acked-v1:${userId || 'anon'}`;
}

/** `{ event_id: isoInstant }`. Entries older than the 30-day floor are dropped on read. */
function readStampMap(storageKey, userId, storage, nowMs) {
  // No user id → no per-user bucket, never a shared `:anon` read.
  if (!userId) return {};
  try {
    const raw = (storage || window.localStorage).getItem(storageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out = {};
    Object.keys(parsed).forEach((id) => {
      const at = Date.parse(parsed[id]);
      if (!id || Number.isNaN(at) || nowMs - at > MAX_LOOKBACK_MS) return;
      out[id] = new Date(at).toISOString();
    });
    return out;
  } catch {
    return {};
  }
}

/** Writes `eventId` into the map and returns the RESULTING map (best effort
 * — a storage failure returns the map unwritten, never throws). */
function writeStamp(storageKey, userId, eventId, storage, nowMs) {
  const current = readStampMap(storageKey, userId, storage, nowMs);
  if (!userId || !eventId) return current;
  // max, never `=` (class B): a lagging clock must not move a stamp back.
  const next = { ...current, [eventId]: maxIso(current[eventId], new Date(nowMs).toISOString()) };
  try {
    (storage || window.localStorage).setItem(storageKey(userId), JSON.stringify(next));
    return next;
  } catch {
    return current;
  }
}

export function readOwnerTransferAnnounced(userId, storage, nowMs = Date.now()) {
  return readStampMap(ownerTransferAnnouncedKey, userId, storage, nowMs);
}

export function readOwnerTransferAcked(userId, storage, nowMs = Date.now()) {
  return readStampMap(ownerTransferAckedKey, userId, storage, nowMs);
}

/** Writes an EXPLICIT instant (rather than "now") — used only by the
 *  merge-from-server path, which must be able to write the server's own
 *  timestamp rather than the moment the merge happens to run. */
function writeStampAt(storageKey, userId, eventId, isoInstant, storage, nowMs = Date.now()) {
  const current = readStampMap(storageKey, userId, storage, nowMs);
  if (!userId || !eventId || !isoInstant) return current;
  const next = { ...current, [eventId]: isoInstant };
  try {
    (storage || window.localStorage).setItem(storageKey(userId), JSON.stringify(next));
    return next;
  } catch {
    return current;
  }
}

function syncOwnerTransferReceipt(userId, eventId, storage) {
  const tenantId = tenantIdForNoticeWrite(userId);
  if (!tenantId) return;
  const announced = readOwnerTransferAnnounced(userId, storage);
  const acked = readOwnerTransferAcked(userId, storage);
  upsertNoticeReceipts({
    tenantId,
    userId,
    rows: [{
      notice_key: `${OWNER_TRANSFER_PREFIX}${eventId}`,
      announced_at: announced[eventId] || null,
      acked_at: acked[eventId] || null,
    }],
  });
}

/** "Later" (✕ / scrim / Escape) — announced only. Never acknowledges. Returns the announced map. */
export function announceOwnerTransfer(userId, eventId, storage, nowMs = Date.now(), opts = {}) {
  const result = writeStamp(ownerTransferAnnouncedKey, userId, eventId, storage, nowMs);
  if (!opts.skipSync) syncOwnerTransferReceipt(userId, eventId, storage);
  return result;
}

/** "Entendido" / "Ver equipo" — acknowledges AND announces. Returns the acked map. */
export function acknowledgeOwnerTransfer(userId, eventId, storage, nowMs = Date.now(), opts = {}) {
  announceOwnerTransfer(userId, eventId, storage, nowMs, { skipSync: true });
  const result = writeStamp(ownerTransferAckedKey, userId, eventId, storage, nowMs);
  if (!opts.skipSync) syncOwnerTransferReceipt(userId, eventId, storage);
  return result;
}

export function ownsOwnerTransferNoticeKey(noticeKey) {
  return typeof noticeKey === 'string' && noticeKey.startsWith(OWNER_TRANSFER_PREFIX);
}

/** Server rows → local maps, MONOTONIC (never move a timestamp backwards). */
export function mergeOwnerTransferReceiptsFromServer(userId, rows, storage) {
  if (!userId || !Array.isArray(rows)) return;
  rows.forEach((row) => {
    if (!ownsOwnerTransferNoticeKey(row?.notice_key)) return;
    const eventId = row.notice_key.slice(OWNER_TRANSFER_PREFIX.length);
    if (!eventId) return;
    if (row.announced_at) {
      const current = readOwnerTransferAnnounced(userId, storage)[eventId];
      if (isNewerIso(row.announced_at, current)) {
        writeStampAt(ownerTransferAnnouncedKey, userId, eventId, row.announced_at, storage);
      }
    }
    if (row.acked_at) {
      const current = readOwnerTransferAcked(userId, storage)[eventId];
      if (isNewerIso(row.acked_at, current)) {
        writeStampAt(ownerTransferAckedKey, userId, eventId, row.acked_at, storage);
      }
    }
  });
}

/** Snapshot for the one-time post-hydration upload. */
export function collectOwnerTransferReceiptsForUpload(userId, storage) {
  if (!userId) return [];
  const announced = readOwnerTransferAnnounced(userId, storage);
  const acked = readOwnerTransferAcked(userId, storage);
  const ids = new Set([...Object.keys(announced), ...Object.keys(acked)]);
  return Array.from(ids).map((eventId) => ({
    notice_key: `${OWNER_TRANSFER_PREFIX}${eventId}`,
    announced_at: announced[eventId] || null,
    acked_at: acked[eventId] || null,
  }));
}
